import { useEffect, useState } from 'react';
import { post } from '../../lib/api.js';
import { formatDateTime, todayLocal } from '../../lib/format.js';
import { Alert, ErrorBox, Field, Modal, useSubmit } from '../../components/ui.js';

/**
 * Editor de sequência (seção 11.3).
 * Mostra a linha do tempo com as datas calculadas ANTES de salvar e recusa
 * intervalo inválido, etapa sem texto e variável desconhecida.
 */

type Unit = 'days' | 'weeks' | 'months' | 'years';

const UNIDADES: { value: Unit; label: string }[] = [
  { value: 'days', label: 'dias' },
  { value: 'weeks', label: 'semanas' },
  { value: 'months', label: 'meses' },
  { value: 'years', label: 'anos' },
];

const MAX_ETAPAS = 10;

interface Etapa {
  offsetAmount: number;
  offsetUnit: Unit;
  messageBody: string;
}

const ETAPAS_INICIAIS: Etapa[] = [
  {
    offsetAmount: 0,
    offsetUnit: 'days',
    messageBody:
      'Olá, {primeiro_nome}! Sou o assistente virtual da {loja}. A última troca de óleo do {veiculo} registrada aqui foi em {data_servico}. ' +
      '{#marco_de_prazo}Já faz {meses_desde_servico} meses desde esse atendimento. {/marco_de_prazo}' +
      '{#tem_intervalo_km}Você já fez outra troca ou rodou cerca de {intervalo_km} km desde então? {/tem_intervalo_km}' +
      '{#sem_intervalo_km}Você já fez outra troca desde então? Se puder, me diga a quilometragem atual. {/sem_intervalo_km}' +
      'Se precisar, posso ajudar com uma dúvida ou consultar um horário para você.',
  },
  {
    offsetAmount: 7,
    offsetUnit: 'days',
    messageBody:
      'Olá, {primeiro_nome}! Aqui é o assistente virtual da {loja}. Você precisa de ajuda com a troca de óleo do {veiculo}? ' +
      'Posso consultar um horário. Se já resolveu, é só me avisar.',
  },
  {
    offsetAmount: 7,
    offsetUnit: 'days',
    messageBody:
      'Olá, {primeiro_nome}! Este é o último lembrete desta sequência da {loja}. Se precisar de suporte ou quiser agendar, ' +
      'pode falar por aqui. Se preferir não receber novos lembretes, é só avisar.',
  },
];

interface TimelineEntry {
  stepNumber: number;
  at: string;
  label: string;
  adjustments: string[];
}

export function EditorSequencia({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('Retorno de troca de óleo');
  const [purpose, setPurpose] = useState('oil_change');
  const [firstAmount, setFirstAmount] = useState(6);
  const [firstUnit, setFirstUnit] = useState<Unit>('months');
  const [etapas, setEtapas] = useState<Etapa[]>(ETAPAS_INICIAIS);
  const [baseDate, setBaseDate] = useState(todayLocal());
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [timelineError, setTimelineError] = useState<unknown>(null);
  const [publicar, setPublicar] = useState(true);


  // Prévia da linha do tempo com as datas reais, sem gravar nada.
  useEffect(() => {
    const timer = setTimeout(() => {
      post<{ timeline: TimelineEntry[] }>('/api/reminder-templates/timeline-preview', {
        baseDate,
        firstOffset: { amount: firstAmount, unit: firstUnit },
        steps: etapas.map((etapa, index) => ({
          stepNumber: index + 1,
          offsetAmount: index === 0 ? 0 : etapa.offsetAmount,
          offsetUnit: etapa.offsetUnit,
          messageBody: etapa.messageBody,
        })),
      })
        .then((r) => {
          setTimeline(r.timeline);
          setTimelineError(null);
        })
        .catch((err: unknown) => {
          setTimeline([]);
          setTimelineError(err);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [baseDate, firstAmount, firstUnit, etapas]);

  const { submit, saving, error } = useSubmit(async () => {
    const created = await post<{ template: { id: string } }>('/api/reminder-templates', {
      name,
      purpose,
      firstOffsetAmount: firstAmount,
      firstOffsetUnit: firstUnit,
      steps: etapas.map((etapa, index) => ({
        stepNumber: index + 1,
        offsetAmount: index === 0 ? 0 : etapa.offsetAmount,
        offsetUnit: etapa.offsetUnit,
        messageBody: etapa.messageBody,
      })),
    });
    if (publicar) {
      await post(`/api/reminder-templates/${created.template.id}/activate`, {});
    }
    onCreated();
  });

  function atualizar(index: number, patch: Partial<Etapa>) {
    setEtapas((atual) => atual.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  }
  function mover(index: number, direcao: -1 | 1) {
    setEtapas((atual) => {
      const destino = index + direcao;
      if (destino < 0 || destino >= atual.length) return atual;
      const copia = [...atual];
      const [item] = copia.splice(index, 1);
      copia.splice(destino, 0, item!);
      return copia;
    });
  }

  return (
    <Modal
      title="Nova sequência de retorno"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-sequencia" className="primary" disabled={saving}>
            {saving ? 'Salvando…' : publicar ? 'Salvar e publicar' : 'Salvar como rascunho'}
          </button>
        </>
      }
    >
      <form id="form-sequencia" onSubmit={submit}>
        <ErrorBox error={error} />

        <div className="field-row">
          <Field label="Nome da sequência" required>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field
            label="Finalidade"
            required
            hint="Identificador do tipo de retorno. O serviço registrado com a mesma finalidade usa esta sequência."
          >
            <input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)} required />
          </Field>
        </div>

        <div className="field-row">
          <Field label="Primeiro contato em" required>
            <input
              type="number"
              min={1}
              value={firstAmount}
              onChange={(e) => setFirstAmount(Math.max(1, Number(e.target.value)))}
              required
            />
          </Field>
          <Field label="Unidade">
            <select value={firstUnit} onChange={(e) => setFirstUnit(e.target.value as Unit)}>
              {UNIDADES.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Simular a partir de" hint="Só para ver as datas. Não grava nada.">
            <input type="date" value={baseDate} onChange={(e) => setBaseDate(e.target.value)} />
          </Field>
        </div>

        <h3 style={{ margin: '16px 0 8px' }}>Etapas</h3>
        {etapas.map((etapa, index) => (
          <div
            key={index}
            style={{
              border: '1px solid var(--gl-border)',
              borderRadius: 6,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div className="row" style={{ marginBottom: 8 }}>
              <strong>Etapa {index + 1}</strong>
              <span className="right row">
                <button type="button" className="small" disabled={index === 0} onClick={() => mover(index, -1)}>
                  Subir
                </button>
                <button
                  type="button"
                  className="small"
                  disabled={index === etapas.length - 1}
                  onClick={() => mover(index, 1)}
                >
                  Descer
                </button>
                <button
                  type="button"
                  className="small danger"
                  disabled={etapas.length === 1}
                  onClick={() => setEtapas((atual) => atual.filter((_, i) => i !== index))}
                >
                  Remover
                </button>
              </span>
            </div>

            {index === 0 ? (
              <p className="hint">Sai no prazo do primeiro contato definido acima.</p>
            ) : (
              <div className="field-row">
                <Field label="Intervalo após o envio aceito da etapa anterior" required>
                  <input
                    type="number"
                    min={1}
                    value={etapa.offsetAmount}
                    onChange={(e) => atualizar(index, { offsetAmount: Math.max(1, Number(e.target.value)) })}
                    required
                  />
                </Field>
                <Field label="Unidade">
                  <select
                    value={etapa.offsetUnit}
                    onChange={(e) => atualizar(index, { offsetUnit: e.target.value as Unit })}
                  >
                    {UNIDADES.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}

            <Field
              label="Mensagem"
              required
              hint="Variáveis: {primeiro_nome} {loja} {veiculo} {data_servico} {meses_desde_servico} {intervalo_km}. Blocos: {#marco_de_prazo}…{/marco_de_prazo}, {#tem_intervalo_km}…{/tem_intervalo_km}, {#sem_intervalo_km}…{/sem_intervalo_km}. Preço e desconto não podem ser escritos à mão."
            >
              <textarea
                value={etapa.messageBody}
                onChange={(e) => atualizar(index, { messageBody: e.target.value })}
                required
              />
            </Field>
          </div>
        ))}

        {etapas.length < MAX_ETAPAS ? (
          <button
            type="button"
            onClick={() =>
              setEtapas((atual) => [
                ...atual,
                { offsetAmount: 7, offsetUnit: 'days', messageBody: 'Olá, {primeiro_nome}! ' },
              ])
            }
          >
            Adicionar etapa
          </button>
        ) : (
          <p className="hint">Limite de {MAX_ETAPAS} etapas. Repetição sem fim não é permitida.</p>
        )}

        <Alert tone="info" title="Como as datas são calculadas">
          A etapa 1 sai {firstAmount} {UNIDADES.find((u) => u.value === firstUnit)?.label} após a data do
          serviço, em meses de calendário. As etapas seguintes contam a partir do envio efetivamente
          aceito da anterior — nunca de uma data presumida.
        </Alert>

        {timelineError ? <ErrorBox error={timelineError} /> : null}

        {timeline.length ? (
          <h3 style={{ margin: '16px 0 8px' }}>Linha do tempo simulada</h3>
        ) : null}
        {timeline.length ? (
          <ul className="list">
            {timeline.map((entry) => (
              <li key={entry.stepNumber}>
                <div className="list-main">
                  <div className="list-title">{entry.label}</div>
                  {entry.adjustments.map((a, i) => (
                    <div key={i} className="list-sub">
                      {a}
                    </div>
                  ))}
                </div>
                <span className="list-when">{formatDateTime(entry.at)}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <label className="checkbox" style={{ marginTop: 12 }}>
          <input type="checkbox" checked={publicar} onChange={(e) => setPublicar(e.target.checked)} />
          Publicar agora (sequência em rascunho não programa retorno)
        </label>
      </form>
    </Modal>
  );
}
